import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const giftCardSchemaBase = z
  .object({
    occasion: z.string().min(1, "Please select an occasion"),
    yourName: z.string().min(1, "Your name is required"),
    title: z.string().min(1, "Title is required"),
    message: z.string().min(1, "Message is required").max(200, "Max 200 characters"),
    amount: z
      .union([z.number(), z.string(), z.null()])
      .refine((val) => Number(val) >= 10, "Please select or enter an amount of at least $10"),
    expirationPeriod: z.union([z.number(), z.string(), z.null()]).optional(),
    quantity: z.union([z.number(), z.string(), z.null()]).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.expirationPeriod &&
      (!Number.isInteger(Number(data.expirationPeriod)) || Number(data.expirationPeriod) <= 0)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["expirationPeriod"],
        message: "Expiration period must be a positive whole number",
      });
    }

    if (data.quantity !== null && data.quantity !== undefined && data.quantity !== "") {
      const quantity = Number(data.quantity);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) {
        ctx.addIssue({
          code: "custom",
          path: ["quantity"],
          message: "Number of gifts must be a whole number between 1 and 100",
        });
      }
    }
  });

export type GiftCardFormData = z.input<typeof giftCardSchemaBase>;

export interface GiftCardFormReturn extends UseFormReturn<GiftCardFormData> {
  defaultValues: GiftCardFormData;
}

const initialFormValues: GiftCardFormData = {
  occasion: "",
  yourName: "",
  title: "A Gift for you",
  message: "Just a little something for you, Hope you enjoy it!",
  amount: null,
  expirationPeriod: null,
  quantity: null,
};

export const useGiftCardForm = (): GiftCardFormReturn => {
  return {
    defaultValues: initialFormValues,
    ...useForm<GiftCardFormData>({
      mode: "onChange",
      defaultValues: initialFormValues,
      resolver: zodResolver(giftCardSchemaBase),
    }),
  };
};
